import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Star, Clock, Calendar, MessageCircle, TrendingUp, ShieldCheck, ChevronDown, Gift, Crown, ArrowRight, Smartphone, BookOpen, Instagram, Calculator, Users } from 'lucide-react';
import { PurchaseNotification } from './components/PurchaseNotification';
import { UrgencyBanner } from './components/UrgencyBanner';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 48,
    seconds: 47
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center justify-center gap-4 text-2xl font-bold text-brand-burgundy my-6" data-id="element-1">
      <div className="flex flex-col items-center" data-id="element-2">
        <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-rose" data-id="element-3">
          {String(timeLeft.hours).padStart(2, '0')}
        </div>
        <span className="text-xs font-normal mt-1 uppercase tracking-wider" data-id="element-4">
          Horas
        </span>
      </div>
      <span className="text-brand-burgundy pb-6" data-id="element-5">:</span>
      <div className="flex flex-col items-center" data-id="element-6">
        <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-rose" data-id="element-7">
          {String(timeLeft.minutes).padStart(2, '0')}
        </div>
        <span className="text-xs font-normal mt-1 uppercase tracking-wider" data-id="element-8">
          Min
        </span>
      </div>
      <span className="text-brand-burgundy pb-6" data-id="element-9">:</span>
      <div className="flex flex-col items-center" data-id="element-10">
        <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-brand-rose" data-id="element-11">
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
        <span className="text-xs font-normal mt-1 uppercase tracking-wider" data-id="element-12">
          Seg
        </span>
      </div>
    </div>;
};
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-brand-rose py-4" data-id="element-13">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left font-semibold text-lg text-brand-burgundy hover:text-brand-dark transition-colors" data-id="element-14">
        {question}
        <ChevronDown className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-15" />
      </button>
      {isOpen && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="mt-3 text-brand-dark/80 leading-relaxed" data-id="element-16">
          {answer}
        </motion.div>}
    </div>;
};
type PlanId = 'kit' | 'pro';
const CHECKOUT_URLS: Record<PlanId, string> = {
  kit: 'https://checkout.solucionesya.shop/cart/49660387066104:1',
  pro: 'https://checkout.solucionesya.shop/cart/49660392603896:1'
};
const PlanSelector = ({
  selected,
  onSelect
}: {
  selected: PlanId;
  onSelect: (plan: PlanId) => void;
}) => {
  const plans: {
    id: PlanId;
    emoji: string;
    name: string;
    sub: string;
    oldPrice: string;
    price: string;
    badge?: string;
  }[] = [{
    id: 'kit',
    emoji: '🎀',
    name: 'KIT AGENDA LLENA',
    sub: '6 Tomos + 3 Bonos',
    oldPrice: '$34.990',
    price: '$19.990'
  }, {
    id: 'pro',
    emoji: '💎',
    name: 'MEGAPACK PRO',
    sub: '6 Tomos + 3 Bonos + 2 Tomos PRO',
    oldPrice: '$43.990',
    price: '$27.990',
    badge: 'MÁS ELEGIDA'
  }];
  return <div className="max-w-3xl mx-auto" data-id="element-17">
      <div className="flex flex-col gap-3 md:gap-4" data-id="element-18">
        {plans.map(plan => {
        const isActive = selected === plan.id;
        const isPro = plan.id === 'pro';
        return <button key={plan.id} type="button" onClick={() => onSelect(plan.id)} aria-pressed={isActive} className={`relative w-full text-left rounded-2xl p-4 md:p-5 border-2 transition-all duration-200 flex items-center gap-4 ${isActive ? isPro ? 'border-brand-gold bg-brand-burgundy shadow-xl' : 'border-brand-burgundy bg-white shadow-xl' : 'border-brand-rose bg-white/70 hover:border-brand-burgundy/40'}`} data-id="element-19">
              {/* Radio indicator */}
              <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isActive ? 'border-brand-gold bg-brand-gold' : 'border-brand-rose bg-white'}`} data-id="element-20">
                {isActive && <span className="w-2.5 h-2.5 rounded-full bg-brand-burgundy" data-id="element-21" />}
              </span>

              <span className="text-2xl md:text-3xl flex-shrink-0" data-id="element-22">
                {plan.emoji}
              </span>

              {/* Name + sub */}
              <div className="min-w-0 flex-grow" data-id="element-23">
                <div className="flex flex-wrap items-center gap-2" data-id="element-24">
                  <h3 className={`font-bold text-sm md:text-lg leading-tight ${isPro && isActive ? 'text-brand-goldlight' : 'text-brand-dark'}`} data-id="element-25">
                    {plan.name}
                  </h3>
                  {plan.badge && <span className="bg-brand-gold text-brand-burgundy px-2 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold tracking-wide uppercase shadow-sm flex items-center gap-1 whitespace-nowrap" data-id="element-26">
                      <Crown size={11} data-id="element-27" /> {plan.badge}
                    </span>}
                </div>
                <p className={`text-xs md:text-sm leading-snug mt-0.5 ${isPro && isActive ? 'text-brand-pink/80' : 'text-brand-dark/60'}`} data-id="element-28">
                  {plan.sub}
                </p>
              </div>

              {/* Prices */}
              <div className="text-right flex-shrink-0" data-id="element-29">
                <div className={`text-lg md:text-2xl font-bold leading-none ${isPro && isActive ? 'text-brand-goldlight' : 'text-brand-burgundy'}`} data-id="element-30">
                  {plan.price}
                </div>
                <div className={`text-[11px] md:text-xs line-through mt-1 ${isPro && isActive ? 'text-brand-pink/50' : 'text-brand-dark/40'}`} data-id="element-31">
                  {plan.oldPrice}
                </div>
              </div>
            </button>;
      })}
      </div>

      <a href={CHECKOUT_URLS[selected]} className="mt-5 flex items-center justify-center gap-2 w-full bg-brand-burgundy hover:bg-brand-burgundy/90 text-white font-bold py-4 rounded-xl transition-all text-base md:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-32">
        {selected === 'pro' ? '💎 COMPRAR MEGAPACK PRO · $27.990' : '🎀 COMPRAR EL KIT · $19.990'}
        <ArrowRight size={20} data-id="element-33" />
      </a>

      <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 mt-4 text-xs text-brand-dark/60 font-medium" data-id="element-34">
        <span className="flex items-center gap-1.5" data-id="element-35">
          <ShieldCheck size={14} data-id="element-36" /> Pago seguro
        </span>
        <span className="flex items-center gap-1.5" data-id="element-37">
          <Clock size={14} data-id="element-38" /> Acceso en 5 min
        </span>
        <span className="flex items-center gap-1.5" data-id="element-39">
          <CheckCircle2 size={14} data-id="element-40" /> Garantía 7 días
        </span>
        <span data-id="element-41">Mercado Pago</span>
      </div>
    </div>;
};
const META_PIXEL_ID = '1012782631449952';
export function App() {
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('pro');
  useEffect(() => {
    let cancelled = false;
    import('meta-pixel').then(mod => {
      if (cancelled) return;
      const ReactPixel = (mod as any).default ?? mod;
      ReactPixel.init(META_PIXEL_ID);
      ReactPixel.pageView();
    }).catch(() => {
      /* no-op if the pixel fails to load */
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return <div className="min-h-screen bg-brand-pink font-sans selection:bg-brand-burgundy selection:text-white overflow-x-hidden" data-id="element-42">
      {/* Top Banner */}
      <div className="bg-brand-burgundy text-white text-center py-2 text-sm font-medium px-4" data-id="element-43">
        Llená tu agenda y cobrá lo que vale en 30 días
      </div>

      {/* Hero Section */}
      <section className="pt-10 md:pt-16 pb-12 md:pb-20 px-4 max-w-3xl mx-auto" data-id="element-44">
        <div className="flex flex-col items-center text-center space-y-6" data-id="element-45">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="inline-block bg-brand-gold/20 text-brand-burgundy px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase border border-brand-gold/30" data-id="element-46">
            El sistema para vivir de tus uñas
          </motion.div>

          {/* Mockup at 70% size */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="w-[70%] mx-auto" data-id="element-47">
            <img src="https://cdn.magicpatterns.com/uploads/wUoAm7HbkTuzAw7Pxk9Tzy/MOCKUP_FONDO_TRANSPARENTE_AGENDA_LLENA.png" alt="Mockup Agenda Llena" width={1920} height={1920} fetchPriority="high" decoding="async" className="w-full h-auto drop-shadow-2xl" data-id="element-48" />
          </motion.div>

          {/* Mini modules below the mockup */}
          <div className="flex flex-wrap justify-center gap-2 text-[11px] font-medium text-brand-dark/60" data-id="element-49">
            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded shadow-sm" data-id="element-50">
              <BookOpen size={12} className="text-brand-gold" data-id="element-51" /> 6+3 Tomos +
              Bonos
            </span>
            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded shadow-sm" data-id="element-52">
              <Smartphone size={12} className="text-brand-gold" data-id="element-53" /> PDF Digital
            </span>
            <span className="flex items-center gap-1 bg-white px-2 py-1 rounded shadow-sm" data-id="element-54">
              <Clock size={12} className="text-brand-gold" data-id="element-55" /> Acceso Inmediato
            </span>
          </div>

          <p className="text-lg md:text-xl text-brand-dark/80 leading-relaxed" data-id="element-56">
            El sistema completo para que dejes de regalar tu trabajo: calculá
            tus precios, llená tu agenda con clientas fijas y cobrá lo que tu
            talento siempre valió.
          </p>

          <div className="pt-2 w-full" data-id="element-57">
            <a href="#pricing" className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-brand-burgundy hover:bg-brand-burgundy/90 text-white text-lg font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1" data-id="element-58">
              💅 QUIERO LLENAR MI AGENDA AHORA
            </a>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm text-brand-dark/60" data-id="element-59">
              <span className="flex items-center gap-1" data-id="element-60">
                <ShieldCheck size={16} data-id="element-61" /> Garantía 7 días
              </span>
              <span data-id="element-62">·</span>
              <span data-id="element-63">Pago seguro</span>
              <span data-id="element-64">·</span>
              <span data-id="element-65">Mercado Pago</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-white py-12 md:py-16 border-y border-brand-rose" data-id="element-66">
        <div className="max-w-6xl mx-auto px-4" data-id="element-67">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-8 md:mb-12 text-center" data-id="element-68">
            <div className="flex items-center gap-2" data-id="element-69">
              <div className="flex text-brand-gold" data-id="element-70">
                {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={24} data-id="element-71" />)}
              </div>
              <span className="font-bold text-xl ml-2" data-id="element-72">4.9/5</span>
            </div>
            <div className="text-brand-dark/70 font-medium text-lg" data-id="element-73">
              +500 manicuras en toda Argentina ya lo usan
            </div>
          </div>

          <div className="flex overflow-x-auto md:grid md:grid-cols-3 gap-4 md:gap-6 pb-4 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-74">
            {[{
            text: 'Subí los precios siguiendo la calculadora y dejé de trabajar casi gratis. En 3 semanas mi facturación cambió por completo. Nunca más copio el precio de la de al lado.',
            author: 'Brenda M.',
            role: 'Buenos Aires, manicura a domicilio'
          }, {
            text: 'Empecé a usar los scripts de WhatsApp y las consultas dejaron de quedarme en visto. Llené la agenda en menos de un mes y ya estoy cobrando seña. Adiós ausentismo.',
            author: 'Carla S.',
            role: 'Rosario, estudio en casa'
          }, {
            text: "El plan de Instagram de 30 días me sacó la cabeza de 'qué subo hoy'. Empezaron a entrarme clientas nuevas por las redes sin gastar un peso en publicidad.",
            author: 'Daiana F.',
            role: 'Córdoba, nail tech soft gel y poligel'
          }].map((testimonial, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-75">
                <div className="min-w-[85vw] md:min-w-0 snap-center bg-brand-pink/50 p-6 rounded-2xl border border-brand-rose h-full flex flex-col" data-id="element-76">
                  <div className="flex text-brand-gold mb-4" data-id="element-77">
                    {[...Array(5)].map((_, j) => <Star key={j} fill="currentColor" size={16} data-id="element-78" />)}
                  </div>
                  <p className="italic text-brand-dark/80 mb-6 flex-grow" data-id="element-79">
                    "{testimonial.text}"
                  </p>
                  <div data-id="element-80">
                    <p className="font-bold text-brand-burgundy" data-id="element-81">
                      {testimonial.author}
                    </p>
                    <p className="text-sm text-brand-dark/60" data-id="element-82">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-12 md:py-20 px-4 max-w-4xl mx-auto" data-id="element-83">
        <div className="text-center mb-8 md:mb-12" data-id="element-84">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-burgundy mb-3 md:mb-4" data-id="element-85">
            ¿TE PASA ESTO?
          </h2>
          <p className="text-lg md:text-xl text-brand-dark/70" data-id="element-86">
            Si hacés uñas en Argentina, esto te suena familiar:
          </p>
        </div>

        <div className="flex overflow-x-auto md:grid md:grid-cols-2 gap-4 pb-4 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-87">
          {[{
          title: 'Cobrás de menos y no te das cuenta',
          desc: 'Pusiste los precios mirando lo que cobra la de al lado, sin saber cuánto te sale realmente cada servicio.'
        }, {
          title: 'La agenda a medias',
          desc: 'Algunas semanas llena, otras vacía. Tu ingreso es una montaña rusa y no sabés cómo estabilizarlo.'
        }, {
          title: 'Consultas que quedan en visto',
          desc: "Te escriben '¿cuánto sale?', respondés, y nunca más sabés de esa persona."
        }, {
          title: 'Clientas que vienen una vez y no vuelven',
          desc: 'No tenés un sistema para que reserven el próximo turno ni para fidelizarlas.'
        }, {
          title: 'Te faltan sin avisar',
          desc: 'Reservás el turno, preparás todo, y no aparece. Plata y tiempo perdidos.'
        }, {
          title: 'Tu Instagram no te trae turnos',
          desc: 'Subís fotos de uñas pero no entra ni una clienta nueva por las redes.'
        }, {
          title: 'No sabés si realmente ganás',
          desc: 'Trabajás todo el día pero mezclás la plata del negocio con la de tu casa y no ves la ganancia real.'
        }].map((pain, i) => <FadeIn key={i} data-id="element-88">
              <div className="min-w-[85vw] md:min-w-0 snap-center flex gap-4 items-start bg-white p-5 rounded-xl shadow-sm border border-brand-rose/50 h-full" data-id="element-89">
                <div className="bg-red-100 p-2 rounded-full text-red-500 mt-1 flex-shrink-0" data-id="element-90">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-91">
                    <path d="M18 6 6 18" data-id="element-92" />
                    <path d="m6 6 12 12" data-id="element-93" />
                  </svg>
                </div>
                <div data-id="element-94">
                  <h3 className="font-bold text-lg text-brand-dark mb-1" data-id="element-95">
                    {pain.title}
                  </h3>
                  <p className="text-brand-dark/70 text-sm md:text-base" data-id="element-96">
                    {pain.desc}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>

        <div className="mt-8 md:mt-12 text-center" data-id="element-97">
          <p className="text-xl md:text-2xl font-serif font-bold text-brand-burgundy italic" data-id="element-98">
            ¿Cuántos meses más vas a trabajar improvisando y regalando tu
            talento?
          </p>
        </div>
      </section>

      {/* Who is it for */}
      <section className="bg-brand-burgundy text-white py-12 md:py-20 px-4" data-id="element-99">
        <div className="max-w-4xl mx-auto" data-id="element-100">
          <h2 className="text-4xl font-serif font-bold text-center mb-12 text-brand-goldlight" data-id="element-101">
            ¿PARA QUIÉN ES?
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-102">
            {[{
            title: 'Manicuras que ya dominan la técnica',
            desc: 'Sabés hacer semipermanente, kapping, soft gel, poligel o nail art, pero te falta el negocio.'
          }, {
            title: 'Trabajás desde casa o a domicilio',
            desc: 'Con tu sillón, tu lámpara y tus herramientas te alcanza. Sin local, sin grandes inversiones.'
          }, {
            title: 'Querés dejar de cobrar migajas',
            desc: 'Estás cansada de trabajar un montón para que no te quede casi nada.'
          }, {
            title: 'Querés una agenda llena y previsible',
            desc: 'Buscás clientas fijas que vuelvan solas, no depender de la suerte mes a mes.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-103">
                <div className="flex gap-4 items-start" data-id="element-104">
                  <CheckCircle2 className="text-brand-gold flex-shrink-0 mt-1" size={24} data-id="element-105" />
                  <div data-id="element-106">
                    <h3 className="font-bold text-xl mb-2 text-brand-pink" data-id="element-107">
                      {item.title}
                    </h3>
                    <p className="text-brand-pink/80 leading-relaxed" data-id="element-108">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <div className="mt-12 text-center" data-id="element-109">
            <p className="text-brand-goldlight/80 mb-4" data-id="element-110">
              ¿No estás segura si es para vos?
            </p>
            <a href="#pricing" className="inline-flex items-center gap-2 text-white font-bold hover:text-brand-gold transition-colors" data-id="element-111">
              Tenés 7 días de garantía <ArrowRight size={20} data-id="element-112" />
            </a>
          </div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-12 md:py-20 px-4 max-w-6xl mx-auto text-center" data-id="element-113">
        <div className="inline-block bg-brand-gold/20 text-brand-burgundy px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase mb-6" data-id="element-114">
          SISTEMA N°1 PARA MANICURAS EN ARGENTINA
        </div>
        <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-burgundy mb-8" data-id="element-115">
          AGENDA LLENA
        </h2>
        <p className="text-xl text-brand-dark/80 max-w-3xl mx-auto mb-12" data-id="element-116">
          Sistema completo · 6 Tomos + 3 Bonos · Para manicuras que ya saben
          hacer uñas · Todo resuelto.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-117">
          {[{
          icon: <BookOpen data-id="element-118" />,
          text: '+90 páginas'
        }, {
          icon: <MessageCircle data-id="element-119" />,
          text: '+100 scripts de WhatsApp'
        }, {
          icon: <Instagram data-id="element-120" />,
          text: '30 placas de Canva'
        }, {
          icon: <Calculator data-id="element-121" />,
          text: 'Calculadoras y planillas'
        }].map((feature, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-rose flex flex-col items-center justify-center gap-3" data-id="element-122">
              <div className="text-brand-gold" data-id="element-123">{feature.icon}</div>
              <span className="font-bold text-brand-dark" data-id="element-124">{feature.text}</span>
            </div>)}
        </div>

        <div className="bg-brand-burgundy/5 rounded-3xl p-8 border border-brand-burgundy/10" data-id="element-125">
          <h3 className="font-bold text-2xl text-brand-burgundy mb-6" data-id="element-126">
            Lo que vas a lograr:
          </h3>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-127">
            {['Calculá tus precios', 'Llená tu agenda', 'Cerrá los turnos', 'Fidelizá clientas', 'Vendé por Instagram', 'Ordená tu plata', 'Frená el ausentismo', 'Cobrá seña'].map((tag, i) => <span key={i} className="bg-white px-4 py-2 rounded-full text-brand-dark font-medium shadow-sm border border-brand-rose/50" data-id="element-128">
                ✓ {tag}
              </span>)}
          </div>
        </div>
      </section>

      {/* Contents (8 Tomos) */}
      <section className="bg-white py-12 md:py-20 px-4 border-y border-brand-rose" data-id="element-129">
        <div className="max-w-5xl mx-auto" data-id="element-130">
          <div className="text-center mb-8 md:mb-12" data-id="element-131">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-brand-burgundy mb-3 md:mb-4" data-id="element-132">
              CONTENIDO COMPLETO
            </h2>
            <p className="text-lg md:text-xl text-brand-dark/70" data-id="element-133">
              8 Tomos · tu hoja de ruta para vivir de tus uñas
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5" data-id="element-134">
            {[{
            num: 'T1',
            title: 'Precios que te dejan ganancia',
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T2',
            title: 'De dónde salen las clientas nuevas',
            img: 'https://images.unsplash.com/photo-1607779097040-26e80aa78e66?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T3',
            title: 'De "¿cuánto sale?" a turno confirmado',
            img: 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T4',
            title: 'Clientas que vuelven cada 21 días',
            img: 'https://images.unsplash.com/photo-1632345031435-8727f6897d53?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T5',
            title: 'Tu Instagram que trabaja por vos',
            img: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T6',
            title: 'Ordená tu negocio y tus números',
            img: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=320&q=55'
          }, {
            num: 'T7',
            title: 'Subí el ticket: servicios premium',
            img: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=320&q=55',
            pro: true
          }, {
            num: 'T8',
            title: 'De vos sola a tu propia marca',
            img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=320&q=55',
            pro: true
          }].map((tomo, i) => <FadeIn key={i} delay={i % 4 * 0.08} data-id="element-135">
                <div className="group bg-brand-pink/30 rounded-2xl border border-brand-rose hover:border-brand-burgundy/40 hover:shadow-md transition-all overflow-hidden h-full flex flex-col" data-id="element-136">
                  <div className="relative aspect-[4/3] overflow-hidden bg-brand-rose/40" data-id="element-137">
                    <img src={tomo.img} alt={tomo.title} loading="lazy" decoding="async" width={320} height={240} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-138" />
                    <span className="absolute top-2 left-2 bg-brand-burgundy text-white rounded-lg w-9 h-9 flex items-center justify-center font-serif font-bold text-sm shadow-md" data-id="element-139">
                      {tomo.num}
                    </span>
                    {tomo.pro && <span className="absolute top-2 right-2 bg-brand-gold text-brand-burgundy text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shadow-sm" data-id="element-140">
                        PRO
                      </span>}
                  </div>
                  <div className="p-3 flex-grow flex items-center" data-id="element-141">
                    <h3 className="text-xs md:text-sm font-bold text-brand-burgundy leading-snug" data-id="element-142">
                      {tomo.title}
                    </h3>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-12 md:py-20 px-4 bg-brand-burgundy text-white relative overflow-hidden" data-id="element-143">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-144">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-gold rounded-full blur-3xl" data-id="element-145"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-pink rounded-full blur-3xl" data-id="element-146"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-147">
          <div className="text-center mb-8 md:mb-16" data-id="element-148">
            <div className="inline-flex items-center gap-2 bg-brand-gold text-brand-burgundy px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase mb-4 md:mb-6" data-id="element-149">
              <Gift size={16} data-id="element-150" /> INCLUIDOS GRATIS
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-brand-goldlight mb-3 md:mb-4" data-id="element-151">
              3 Bonos Estratégicos
            </h2>
            <p className="text-lg md:text-xl text-brand-pink/80" data-id="element-152">
              Sin costo extra — incluidos en todas las ofertas:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-id="element-153">
            {[{
            title: 'Banco de +100 Mensajes de WhatsApp',
            desc: 'Copiá y pegá para cada situación: consulta, regateo, seña, recordatorio y fidelización.',
            img: 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?auto=format&fit=crop&w=400&q=55'
          }, {
            title: '30 Placas de Canva Editables',
            desc: 'Precios, promos, antes/después y testimonios. Cambiás colores y logo, y listo.',
            img: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=400&q=55'
          }, {
            title: 'Calendario de Fechas que Venden (2026)',
            desc: 'Qué promo lanzar en cada fecha clave del año, con la placa lista para usar.',
            img: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=400&q=55'
          }].map((bono, i) => <FadeIn key={i} delay={i * 0.15} data-id="element-154">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 h-full flex flex-col overflow-hidden" data-id="element-155">
                  <div className="relative aspect-[16/9] overflow-hidden bg-white/5" data-id="element-156">
                    <img src={bono.img} alt={bono.title} loading="lazy" decoding="async" width={400} height={225} className="w-full h-full object-cover" data-id="element-157" />
                    <span className="absolute top-2 left-2 bg-brand-gold text-brand-burgundy px-2.5 py-0.5 rounded-md font-bold text-xs shadow-sm" data-id="element-158">
                      Bono {i + 1} · GRATIS
                    </span>
                  </div>
                  <div className="p-4 md:p-5 flex flex-col flex-grow" data-id="element-159">
                    <h3 className="text-base md:text-lg font-bold text-brand-goldlight mb-2" data-id="element-160">
                      {bono.title}
                    </h3>
                    <p className="text-brand-pink/90 leading-relaxed flex-grow text-sm" data-id="element-161">
                      {bono.desc}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <div className="mt-8 md:mt-12 text-center" data-id="element-162">
            <p className="text-xl md:text-2xl font-bold text-brand-goldlight border-t border-white/20 pt-6 md:pt-8 inline-block" data-id="element-163">
              VALOR TOTAL DE LOS BONOS:{' '}
              <span className="line-through opacity-70" data-id="element-164">$34.000</span> — GRATIS
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 md:py-20 px-4 max-w-4xl mx-auto" data-id="element-165">
        <div className="text-center mb-12" data-id="element-166">
          <h2 className="text-4xl font-serif font-bold text-brand-burgundy mb-4" data-id="element-167">
            LA REALIDAD
          </h2>
          <p className="text-xl text-brand-dark/70" data-id="element-168">
            ¿Cuánto cuesta NO tenerlo?
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-brand-rose overflow-hidden" data-id="element-169">
          <div className="grid grid-cols-2 bg-brand-pink/50 border-b border-brand-rose" data-id="element-170">
            <div className="p-4 md:p-6 text-center font-bold text-brand-dark/60" data-id="element-171">
              Sin el sistema
            </div>
            <div className="p-4 md:p-6 text-center font-bold text-brand-burgundy bg-brand-burgundy/5" data-id="element-172">
              Con AGENDA LLENA
            </div>
          </div>

          {[['Cobrás precios al azar y trabajás casi gratis', 'Calculadora que te dice cuánto cobrar para ganar'], ['Agenda vacía a mitad de mes', 'Sistema para llenar la agenda todas las semanas'], ['Consultas que quedan en visto', '+100 scripts que cierran el turno'], ['Clientas que vienen una vez y no vuelven', 'Sistema para que vuelvan solas cada 21 días'], ['Te faltan sin avisar', 'La seña que termina con el ausentismo'], ['Instagram que no te trae nada', 'Plan de 30 días que convierte seguidoras en turnos'], ['No sabés cuánto ganás de verdad', 'Planillas simples de ingresos y ganancia real'], ['Improvisás cada promo y perdés las fechas', 'Calendario comercial 2026 con todo marcado']].map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-brand-rose/50 last:border-0" data-id="element-173">
              <div className="p-4 md:p-6 text-brand-dark/70 text-sm md:text-base flex items-center gap-3" data-id="element-174">
                <div className="text-red-400 flex-shrink-0" data-id="element-175">✕</div>
                {row[0]}
              </div>
              <div className="p-4 md:p-6 text-brand-dark font-medium text-sm md:text-base bg-brand-burgundy/5 flex items-center gap-3" data-id="element-176">
                <div className="text-green-500 flex-shrink-0" data-id="element-177">✓</div>
                {row[1]}
              </div>
            </div>)}
        </div>
      </section>

      {/* Pricing / Offer */}
      <section id="pricing" className="py-12 md:py-20 px-4 bg-brand-pink border-t border-brand-rose" data-id="element-178">
        <div className="max-w-5xl mx-auto" data-id="element-179">
          <div className="text-center mb-8" data-id="element-180">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase animate-pulse" data-id="element-181">
              OFERTA DE LANZAMIENTO
            </div>
          </div>

          <UrgencyBanner data-id="element-182" />
          <PlanSelector selected={selectedPlan} onSelect={setSelectedPlan} data-id="element-183" />
        </div>
      </section>

      {/* PRO Upsell Explanation */}
      <section className="py-12 md:py-20 px-4 max-w-4xl mx-auto" data-id="element-184">
        <div className="bg-white rounded-3xl p-8 md:p-12 border border-brand-gold/30 shadow-xl relative overflow-hidden" data-id="element-185">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold/5 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-186"></div>

          <div className="relative z-10" data-id="element-187">
            <h2 className="text-3xl font-serif font-bold text-brand-burgundy mb-4 text-center" data-id="element-188">
              ¿QUÉ AGREGA EL MEGAPACK PRO?
            </h2>
            <p className="text-center text-brand-dark/70 mb-10" data-id="element-189">
              Para las manicuras que no solo quieren llenar la agenda, sino
              ganar más y construir su marca:
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-190">
              <div className="bg-brand-pink/30 p-6 rounded-2xl border border-brand-rose" data-id="element-191">
                <h4 className="font-bold text-brand-dark mb-2 flex items-center gap-2" data-id="element-192">
                  <span className="text-red-400" data-id="element-193">✕</span> Sin el PRO
                </h4>
                <p className="text-brand-dark/70 text-sm" data-id="element-194">
                  Cobrás siempre lo mismo, los servicios base, y dejás plata
                  sobre la mesa en cada clienta.
                </p>
              </div>
              <div className="bg-brand-gold/10 p-6 rounded-2xl border border-brand-gold/30" data-id="element-195">
                <h4 className="font-bold text-brand-burgundy mb-2 flex items-center gap-2" data-id="element-196">
                  <span className="text-green-500" data-id="element-197">✓</span> Con el PRO
                </h4>
                <p className="text-brand-dark/80 text-sm font-medium" data-id="element-198">
                  Aprendés a subir el ticket con servicios premium y combos, y a
                  escalar de trabajar sola a tener tu propia marca.
                </p>
              </div>
            </div>

            <h3 className="font-bold text-xl text-center mb-8 uppercase tracking-wide text-brand-dark" data-id="element-199">
              EL MEGAPACK PRO INCLUYE 2 TOMOS EXCLUSIVOS:
            </h3>

            <div className="space-y-6" data-id="element-200">
              <div className="flex gap-4" data-id="element-201">
                <div className="w-12 h-12 bg-brand-gold text-brand-burgundy rounded-xl flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-sm" data-id="element-202">
                  T7
                </div>
                <div data-id="element-203">
                  <h4 className="font-bold text-lg text-brand-burgundy mb-1" data-id="element-204">
                    Subí el ticket: servicios premium y combos
                  </h4>
                  <p className="text-brand-dark/70" data-id="element-205">
                    Cómo ganar más con las MISMAS clientas: nail art,
                    encapsulados, 3D, efecto cromado, combos y upsell en la
                    silla sin sonar a venta.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-206">
                <div className="w-12 h-12 bg-brand-gold text-brand-burgundy rounded-xl flex items-center justify-center font-bold text-xl flex-shrink-0 shadow-sm" data-id="element-207">
                  T8
                </div>
                <div data-id="element-208">
                  <h4 className="font-bold text-lg text-brand-burgundy mb-1" data-id="element-209">
                    De vos sola a tu propia marca
                  </h4>
                  <p className="text-brand-dark/70" data-id="element-210">
                    El salto a estudio/marca propia, sumar una asistenta o
                    alumna, dar clases como segunda fuente de ingreso y vender
                    productos a tu comunidad.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-12 md:py-16 px-4 bg-brand-burgundy text-white text-center" data-id="element-211">
        <div className="max-w-3xl mx-auto" data-id="element-212">
          <ShieldCheck size={64} className="mx-auto text-brand-gold mb-6" data-id="element-213" />
          <h2 className="text-4xl font-serif font-bold text-brand-goldlight mb-6" data-id="element-214">
            7 DÍAS GARANTÍA TOTAL
          </h2>
          <p className="text-xl text-brand-pink/90 leading-relaxed" data-id="element-215">
            7 días para probarlo o te devolvemos el 100%. Descargás los tomos,
            usás los scripts, probás las plantillas con tus clientas. Si no es
            lo que esperabas: sin preguntas, sin formularios, sin drama.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-20 px-4 max-w-3xl mx-auto" data-id="element-216">
        <div className="text-center mb-12" data-id="element-217">
          <h2 className="text-4xl font-serif font-bold text-brand-burgundy mb-4" data-id="element-218">
            DUDAS COMUNES
          </h2>
          <p className="text-xl text-brand-dark/70" data-id="element-219">Preguntas Frecuentes</p>
        </div>

        <div className="bg-white p-8 rounded-3xl shadow-sm border border-brand-rose" data-id="element-220">
          {[{
          q: '¿Cómo recibo el material después de comprar?',
          a: 'Apenas se acredita el pago por Mercado Pago, recibís el acceso para descargar todos los PDF al instante. En menos de 5 minutos ya lo tenés.'
        }, {
          q: '¿Sirve si recién empiezo con las uñas?',
          a: 'El KIT está pensado para manicuras que ya saben hacer uñas y quieren vivir de eso. No enseña técnica: enseña el negocio (precios, clientas, agenda, redes). Si ya atendés aunque sea a pocas clientas, te sirve desde el día uno.'
        }, {
          q: '¿Necesito saber de diseño o de Excel?',
          a: 'No. Las placas se editan en Canva con cambiar colores y logo, y las planillas son simples e imprimibles. Todo está listo para usar, sin complicaciones.'
        }, {
          q: '¿Los 3 bonos están incluidos en las dos ofertas?',
          a: 'Sí. Tanto el KIT como el MEGAPACK PRO incluyen los 3 bonos completos sin costo extra.'
        }, {
          q: '¿En qué se diferencia del contenido gratis de Instagram o YouTube?',
          a: 'En que esto es un sistema ordenado y completo, no tips sueltos. Tenés todo resuelto y aplicable: calculadoras, scripts, plantillas y un plan paso a paso, sin perder horas buscando.'
        }, {
          q: '¿Por qué elegir el MEGAPACK PRO y no el KIT?',
          a: 'El KIT te enseña a llenar la agenda. El MEGAPACK PRO suma cómo ganar más por clienta (servicios premium y combos) y cómo escalar a tu propia marca. Por solo $8.000 más, te llevás el plan completo de crecimiento.'
        }, {
          q: '¿Tiene garantía real?',
          a: 'Sí. 7 días para probarlo. Si no te convence, te devolvemos el 100%, sin vueltas.'
        }].map((faq, i) => <AccordionItem key={i} question={faq.q} answer={faq.a} data-id="element-221" />)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 px-4 text-center bg-brand-pink border-t border-brand-rose" data-id="element-222">
        <div className="max-w-3xl mx-auto" data-id="element-223">
          <UrgencyBanner data-id="element-224" />
          <PlanSelector selected={selectedPlan} onSelect={setSelectedPlan} data-id="element-225" />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-dark text-white/60 py-12 px-4 text-center text-sm" data-id="element-226">
        <div className="max-w-4xl mx-auto" data-id="element-227">
          <div className="text-2xl font-serif font-bold text-white mb-4" data-id="element-228">
            💅 AGENDA LLENA
          </div>
          <p className="mb-8 max-w-xl mx-auto" data-id="element-229">
            El sistema N°1 para manicuras emprendedoras en Argentina. Porque tu
            talento merece una agenda llena y un precio justo, no semanas vacías
            ni trabajar casi gratis.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8" data-id="element-230">
            <a href="#" className="hover:text-white transition-colors" data-id="element-231">
              contacto@solucionesya.shop
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-232">
              @solucionesya.shop
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-233">
              solucionesya.shop
            </a>
          </div>
          <p data-id="element-234">© Solucionesya.shop 2026 — Todos los derechos reservados.</p>
        </div>
      </footer>

      {/* Live social-proof purchase notifications */}
      <PurchaseNotification data-id="element-235" />
    </div>;
}